import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, History, User, ArrowRight, Users, Gift, Package, CheckCircle2, Coins } from 'lucide-react';
import axios from '../api/client';
import { API } from '../api/config';

interface ActivityEntry {
  key: string;
  icon: React.ReactNode;
  label: string;
  date: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    if (!user) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    try {
      const [ordersRes, rewardsRes] = await Promise.all([
        axios.get(API.listOrders(), { headers }).catch(() => ({ data: [] })),
        axios.get(API.listRewardTransactions(), { headers }).catch(() => ({ data: [] })),
      ]);

      const entries: ActivityEntry[] = [];

      for (const order of ordersRes.data || []) {
        const shortId = (order.id || '').slice(0, 8).toUpperCase();
        if (order.created_at) {
          entries.push({
            key: `${order.id}-placed`,
            icon: <Package size={16} />,
            label: `Order #${shortId} placed — £${Number(order.total_amount || 0).toFixed(2)}`,
            date: order.created_at,
          });
        }
        if (order.delivered_at) {
          entries.push({
            key: `${order.id}-delivered`,
            icon: <CheckCircle2 size={16} />,
            label: `Order #${shortId} delivered`,
            date: order.delivered_at,
          });
        }
      }

      for (const txn of rewardsRes.data || []) {
        entries.push({
          key: txn.id,
          icon: <Coins size={16} />,
          label: `${txn.description || 'Reward transaction'} (${txn.amount >= 0 ? '+' : ''}£${Number(txn.amount).toFixed(2)})`,
          date: txn.created_at,
        });
      }

      entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setActivity(entries.slice(0, 6));
    } catch (err) {
      console.error('Failed to load recent activity:', err);
    } finally {
      setActivityLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  if (!user) return <div>Not authenticated</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <h1 className="page-title" style={{ margin: 0 }} data-testid="user-greeting">
          {t('dashboard.welcome', { name: user.full_name || t('dashboard.welcomeDefault') })}
        </h1>
        {user.is_admin && (
          <Link to="/admin/users" className="btn btn-primary">
            <Users size={16} />
            User Management
          </Link>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '40px' }}>
        <div className="section-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '10px', background: '#EFF6FF', color: '#2563EB', borderRadius: '10px' }}>
                <User size={24} />
              </div>
              <h3 style={{ margin: 0 }}>Profile</h3>
            </div>
            <p style={{ color: '#64748B', fontSize: '14px', lineHeight: 1.6 }}>
              Keep your personal information and bank details up to date for faster payouts.
            </p>
          </div>
          <Link to="/profile" className="btn btn-outline" style={{ marginTop: '24px', width: 'fit-content' }}>
            Manage Profile <ArrowRight size={16} />
          </Link>
        </div>

        <div className="section-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '10px', background: '#FFF7ED', color: '#D4AF37', borderRadius: '10px' }}>
                <Gift size={24} />
              </div>
              <h3 style={{ margin: 0 }}>Rewards</h3>
            </div>
            <p style={{ color: '#64748B', fontSize: '14px', lineHeight: 1.6 }}>
              View your cash reward balance and transaction history. Bigger orders earn more back.
            </p>
          </div>
          <Link to="/rewards" className="btn btn-outline" style={{ marginTop: '24px', width: 'fit-content' }}>
            View Rewards <ArrowRight size={16} />
          </Link>
        </div>

        <div className="section-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '10px', background: '#F5F3FF', color: '#7C3AED', borderRadius: '10px' }}>
                <CreditCard size={24} />
              </div>
              <h3 style={{ margin: 0 }}>Smart Orders</h3>
            </div>
            <p style={{ color: '#64748B', fontSize: '14px', lineHeight: 1.6 }}>
              Request new quotes and track your UK procurement orders.
            </p>
          </div>
          <Link to="/orders" className="btn btn-outline" style={{ marginTop: '24px', width: 'fit-content' }}>
            View Orders <ArrowRight size={16} />
          </Link>
        </div>

      </div>

      <div className="section-card">
        <h3 className="card-title">Recent Activity</h3>
        {activityLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748B' }}>
            <p>Loading...</p>
          </div>
        ) : activity.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748B' }}>
            <History size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
            <p>No recent activity found.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '2px', marginTop: '8px' }}>
            {activity.map((entry) => (
              <div
                key={entry.key}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', borderBottom: '1px solid var(--sidebar-border)' }}
              >
                <div style={{ padding: '8px', background: '#F8FAFC', borderRadius: '8px', color: '#475569', flexShrink: 0 }}>
                  {entry.icon}
                </div>
                <div style={{ flex: 1, fontSize: '14px' }}>{entry.label}</div>
                <div style={{ fontSize: '12px', color: '#94A3B8', flexShrink: 0 }}>
                  {new Date(entry.date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
